import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  FormControl,
  formControlCustomRule,
  formControlMinLengthRule,
} from '@app/common/components/controls';
import { FieldComponent } from '@app/common/configurable-wizards';
import { MaskedTextFieldFragment } from '@app/common/configurable-wizards/queries/queryTyping/all-fields';
import { combineNames, getFieldErrorMessage } from '@app/common/configurable-wizards/utils';
import { DEFAULT_COUNTRY } from '@app/common/types/locales';
import { Country, findCountryByCode, findCountryByPhone } from '@app/common/utils/countries';
import { useCommonValidationMessages } from '@app/common/valiation/commonValidationMessages';

import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';

import { InternationalPhoneControl } from './InternationalPhoneControl';
import styles from './InternationalPhoneField.pcss';

/**
 * Minimum numbers length of phone number.
 */
export const MIN_PHONE_LENGTH = 12;

/**
 * Maximum numbers length of phone number.
 */
export const MAX_PHONE_LENGTH = 16;

/**
 * Represents the InternationalPhone configurable field.
 * Used for contacts and settings phone number.
 */
export const InternationalPhoneField: FieldComponent<MaskedTextFieldFragment> = ({ parent, field }) => {
  const { t } = useUserSettingsTranslation();
  const { setValue, formState: { errors } } = useFormContext();
  const name = combineNames(parent, field.id);
  const [country, setCountry] = useState(
    field.sValue
      ? findCountryByPhone(field.sValue)
      : findCountryByCode(DEFAULT_COUNTRY),
  );

  useEffect(() => {
    setValue(name, field.sValue);
  }, []);

  const getFormat = () => {
    const dialCodeLength = country?.dialCode.length || 0;

    return String().padEnd(MAX_PHONE_LENGTH - dialCodeLength, '#');
  };

  const commonValidationMessages = useCommonValidationMessages();
  const getErrorMessage = useCallback(
    () => getFieldErrorMessage(name, errors, commonValidationMessages),
    [name, errors],
  );

  const validationRules = React.useMemo(() => {
    const { countryCode, dialCode, areaCodes } = country || {};
    const isDefaultCountry = countryCode === DEFAULT_COUNTRY;

    return [
      formControlCustomRule(
        // eslint-disable-next-line i18next/no-literal-string
        'phoneFormat',
        (value: string) => (
          isDefaultCountry
            ? /\+1[2-9][0-8]\d[2-9]\d{2}\d{4}/.test(value) || t(
              'international-phone-field.validation.phone-format|Message for phone number format validation rule',
              'Please enter a valid phone number',
            )
            : true
        ),
      ),
      formControlCustomRule(
        // eslint-disable-next-line i18next/no-literal-string
        'areaCode',
        (value: string) => (
          !isDefaultCountry && areaCodes
            ? areaCodes.some((code) => value.startsWith(`${dialCode}${code}`)) || t(
              'international-phone-field.validation.area-code|Message for phone number area code validation rule',
              'The area code you\'ve entered doesn\'t correspond to the country selected. Please verify and try again',
            )
            : true
        ),
      ),
      formControlMinLengthRule(
        MIN_PHONE_LENGTH,
        t(
          'international-phone-field.validation.min-length|Message for phone number minLength validation rule',
          'Please enter your complete phone number',
        ),
      ),
    ];
  }, [country]);

  return (
    <FormControl
      name={name}
      validations={validationRules}
      getErrorMessage={getErrorMessage}
      className={styles.formControl}
    >
      <InternationalPhoneControl
        format={getFormat()}
        defaultValue={field.sValue || ''}
        onCountryChange={(item: Country) => {
          setCountry(item);
        }}
      />
    </FormControl>
  );
};
